import './table-row.scss';
import React, { useMemo, useRef, useState } from 'react';
import { Column, CrudConfig, flattenColumns } from '../../../model/table';
import { TableRowActions } from '../table-row-actions/table-row-actions';
import { EditorMode, TableRowMode } from '../../../utils/enums';
import { observer } from 'mobx-react-lite';
import { Menu, Popover, Tooltip } from 'antd';
import { useDrag, useDrop } from 'react-dnd';
import CustomIcon, { IconType } from '../custom-icon/custom-icon';

export interface TableAction<T> {
    icon: IconType;
    label: (rowData: T) => string;
    handler: (rowData: T) => void;
    disabled?: (rowData:T) => boolean;
    tooltip?: string;
}


interface TableRowProps<T> extends React.HTMLAttributes<HTMLDivElement> {
    columnsConfig: Column<T>[];
    data: T;
    crudConfig?: CrudConfig<T>;
    index: number;
    initialMode?: TableRowMode;
    openToEdit?: (rowData: T, modalMode: EditorMode) => void;
    isInactive?: (rowData: T) => boolean;
    isDraggable?: (rowData: T) => boolean;
    moveRow?: (dragIndex: number, hoverIndex: number) => void;
    actions?: TableAction<T>[];
    lastItemIndex?: number;
}

export const TableRow =
    observer(<T,>({ columnsConfig, data, crudConfig, index, initialMode, openToEdit, isInactive, actions, isDraggable, moveRow, lastItemIndex, ...otherProps }
                      : TableRowProps<T>) => {
        const [isPopoverOpen, setIsPopoverOpen] = useState(false);
        const [isDropNotAllowed, setIsDropNotAllowed] = useState(false);


        const actionItems = actions?.filter(_ => !_.disabled?.(data)).map(_ => {
            return {
            key: _.label(data),
            label: (
                <Tooltip>
                    <button type="button" className="custom-menu-item" onClick={() => {
                        setIsPopoverOpen(false);
                        _.handler(data);
                    }}>
                        <CustomIcon type={_.icon}/>
                        {_.label(data)}
                    </button>
                </Tooltip>
            )
        };
    }) || [];

        const handleOpenChange = (visible:boolean) => {
            setIsPopoverOpen(visible);
        };

    const actionsDropdown = actionItems?.length ? (
        <Popover
            content={<Menu items={actionItems} />}
            trigger="click"
            placement="bottomRight"
            showArrow={false}
            open={isPopoverOpen}
            onOpenChange={handleOpenChange}
            overlayClassName={'popover-overlay-menu table-action-menu'}>
            <div className={`three-dots-icon-wrapper`}>
                <div className="three-dots-icon"/>
            </div>
        </Popover>
    ) : null;


    const ref = useRef<HTMLDivElement | null>(null);
    const getColumnContent = (column: Column<T>) => {
        if (column.getDisplayElement) {
            return column.getDisplayElement(column, data);
        }
        return data[column.accessor];
    };

    const flatColumns = useMemo(() => flattenColumns(columnsConfig), [columnsConfig]);

    const draggable =  isDraggable?.(data);

    const [, dropRef] = useDrop({
        accept: 'item',
        drop: (item: { index: number }, monitor) => {
            const dragIndex = item.index;
            const dropIndex = index;
            if (dragIndex === dropIndex) {
                return;
            }
            if (dropIndex === lastItemIndex) {
                setIsDropNotAllowed(true);
                return;
            }
            setIsDropNotAllowed(false);
            moveRow?.(dragIndex, dropIndex);
        }
    }, [index,moveRow]);

    const [{ isDragging }, dragRef] = useDrag(() => ({
        type: 'item',
        item: {index},
        canDrag: draggable,
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }), [index, draggable]);

        const dragDropRef = isDraggable ? dragRef(dropRef(ref)) as React.LegacyRef<HTMLDivElement> : null;
        // eslint-disable-next-line sonarjs/no-nested-template-literals

        const inactiveClass = isInactive?.(data) ? 'inactive' : '';
        const draggingClass = isDragging ? 'drag' : '';
        const tableRowClassName = `${inactiveClass} ${draggingClass}`;

        const getStickyClass = (sticky: string|undefined) => {
            if(sticky === 'first') {
                return 'table-sticky-col-first';
            }else if(sticky === 'second') {
                return 'table-sticky-col-second';
            } else {
                return '';
            }
        };

        const dropAlertTooltip = isDropNotAllowed && (<Popover
                content={'rule cant be position after Drop All rule'}
                trigger="click"
                placement="topLeft"
                showArrow={false}
                open={isDropNotAllowed}
                onOpenChange={() => setIsDropNotAllowed(!isDropNotAllowed)}
                overlayClassName={'drop-not-allowed-tooltip'} />
        );

        return <div key={index} className={`table-row ${tableRowClassName}`} tabIndex={-1} {...otherProps}
                    ref={dragDropRef}>
            {flatColumns.map((column, indexX) =>
                <div
                    className={`table-cell  ${getStickyClass(column.sticky)} ${column.accessor}`}
                    data-testid={`cell-${index}-${indexX}`}
                    key={`${indexX}-${column.accessor}`}
                    style={{minWidth: column.width, width: column.width}}>
                    {indexX === 0 && <>{dropAlertTooltip}</>}
                    {getColumnContent(column) as React.ReactNode}
                </div>
            )}
        {(crudConfig?.displayInlineActions || crudConfig?.customActions) &&
            <TableRowActions<T>
                crudConfig={crudConfig}
                rowData={data}
                openToEdit={() => openToEdit?.(data, EditorMode.EDIT)}
            />}
        {actionsDropdown && (
            <div className={`table-cell actions-cell`}>
                {actionsDropdown}
            </div>
        )}
    </div>;
});
